import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'

import { Activity } from 'react'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import DeleteIcon from '@mui/icons-material/Delete'
import adjustColorContrast from '../../utilities/adjustColorContrast'
import { getPaletteColor, MAX_PARAMETERS } from '../../utilities/paletteColors'

const AnalysisChainForm = ({
  analysisChain,
  onInputChange,
  analyses,
  dataTypes,
  onAnalysisChange,
  onDataTypeChange,
  onAddAnalysis,
  onRemoveAnalysis,
  onParameterChange,
  onAddParameter,
  onRemoveParameter,
  onNewParameterChange,
  newParameter,
  hasAvailableSubsequentAnalyses,
  onSubmit,
  onSaveAsNew,
  saving,
  errors,
  isEditMode,
}) => {
  const theme = useTheme()
  const selectedFirstAnalysis =
    analyses.find((a) => a._id === analysisChain.analysis_ids?.[0]) || null

  return (
    <Box component="form" onSubmit={onSubmit}>
      <Grid container spacing={3}>
        <Paper sx={{ width: '100%', padding: 5 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Analysis Chain Details
          </Typography>
          <TextField
            label="Name"
            name="name"
            value={analysisChain?.name || ''}
            onChange={onInputChange}
            fullWidth
            margin="normal"
            error={!!errors.name}
            helperText={errors.name}
          />
          <TextField
            label="Description"
            name="description"
            value={analysisChain?.description || ''}
            onChange={onInputChange}
            fullWidth
            margin="normal"
            multiline
            rows={3}
            error={!!errors.description}
            helperText={errors.description}
          />

          <Grid size={12}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Analyses Configuration
            </Typography>
            <Stack spacing={2}>
              <FormControl fullWidth>
                <InputLabel>First Analysis</InputLabel>
                <Select
                  value={analysisChain.analysis_ids?.[0] || ''}
                  onChange={(e) => onAnalysisChange(0, e.target.value)}
                >
                  {analyses.map((analysis) => (
                    <MenuItem key={analysis._id} value={analysis._id}>
                      {analysis.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {selectedFirstAnalysis && (
                <>
                  <FormControl fullWidth>
                    <InputLabel>Chain Input Data Type</InputLabel>
                    <Select
                      multiple
                      value={analysisChain.input_datatype_ids || []}
                      onChange={(e) => onDataTypeChange(0, e.target.value)}
                    >
                      {selectedFirstAnalysis.inputDataTypes.map((dataType) => (
                        <MenuItem key={dataType._id} value={dataType._id}>
                          <Chip label={dataType.name}></Chip>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {
                    <Stack variant="outlined" sx={{ p: 2 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 2,
                          alignItems: 'flex-start',
                          mt: 2,
                        }}
                      >
                        <TextField
                          label="Parameter Key"
                          value={
                            newParameter[selectedFirstAnalysis._id]?.key || ''
                          }
                          onChange={(e) =>
                            onNewParameterChange(
                              selectedFirstAnalysis._id,
                              'key',
                              e.target.value,
                            )
                          }
                          sx={{ flex: 1 }}
                        />
                        <TextField
                          label="Parameter Value"
                          value={
                            newParameter[selectedFirstAnalysis._id]?.value || ''
                          }
                          onChange={(e) =>
                            onNewParameterChange(
                              selectedFirstAnalysis._id,
                              'value',
                              e.target.value,
                            )
                          }
                          sx={{ flex: 1 }}
                        />
                        <Button
                          variant="outlined"
                          onClick={() =>
                            onAddParameter(selectedFirstAnalysis._id)
                          }
                          sx={{ height: '56px' }}
                        >
                          Add
                        </Button>
                      </Box>
                      <Box
                        sx={{
                          mt: 2,
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 1,
                        }}
                      >
                        {Object.entries(
                          analysisChain.analysis_parameters?.[
                            selectedFirstAnalysis._id
                          ] || {},
                        ).map(([key, value], index) => {
                          const chipColor = adjustColorContrast(
                            getPaletteColor('spectral', index),
                            theme,
                          )
                          return (
                            <Chip
                              key={key}
                              label={`${key}: ${value}`}
                              onDelete={() =>
                                onRemoveParameter(
                                  selectedFirstAnalysis._id,
                                  key,
                                )
                              }
                              sx={{
                                borderColor: chipColor,
                                color: chipColor,
                              }}
                              variant="outlined"
                            />
                          )
                        })}
                      </Box>
                    </Stack>
                  }
                </>
              )}

              {analysisChain.analysis_ids?.slice(1).map((analysisId, index) => {
                const analysis = analyses.find((a) => a._id === analysisId)
                const previousAnalysisId = analysisChain.analysis_ids[index]
                const previousAnalysis = analyses.find(
                  (a) => a._id === previousAnalysisId,
                )

                const previousAnalysisOutputDataType = dataTypes.find(
                  (dt) => dt.analysisId === previousAnalysis?._id,
                )

                const filteredAnalyses = analyses.filter((currentAnalysis) => {
                  return currentAnalysis.input_datatype_ids.some(
                    (dt) => dt === previousAnalysisOutputDataType?._id,
                  )
                })

                return (
                  <Stack spacing={2} key={index}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <FormControl fullWidth>
                        <InputLabel>Subsequent Analysis</InputLabel>
                        <Select
                          value={analysisId}
                          onChange={(e) =>
                            onAnalysisChange(index + 1, e.target.value)
                          }
                        >
                          {filteredAnalyses.map((analysis) => (
                            <MenuItem key={analysis._id} value={analysis._id}>
                              {analysis.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <IconButton onClick={() => onRemoveAnalysis(index + 1)}>
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                    {analysis && (
                      <>
                        {Object.keys(analysis.parameters).length > 0 && (
                          <Card variant="outlined" sx={{ p: 2 }}>
                            <Stack
                              direction="row"
                              alignItems="center"
                              justifyContent="space-between"
                              sx={{ mb: 1 }}
                            >
                              <Typography variant="subtitle1">
                                Parameters
                              </Typography>
                              <Button
                                startIcon={<AddCircleOutlineIcon />}
                                onClick={() => onAddParameter(analysis._id)}
                                size="small"
                              >
                                Add Parameter
                              </Button>
                            </Stack>
                            <Stack>
                              <Box
                                sx={{
                                  display: 'flex',
                                  gap: 2,
                                  alignItems: 'flex-start',
                                  mt: 2,
                                }}
                              >
                                <TextField
                                  label="Parameter Key"
                                  value={newParameter[analysis._id]?.key || ''}
                                  onChange={(e) =>
                                    onNewParameterChange(
                                      analysis._id,
                                      'key',
                                      e.target.value,
                                    )
                                  }
                                  sx={{ flex: 1 }}
                                />
                                <TextField
                                  label="Parameter Value"
                                  value={
                                    newParameter[analysis._id]?.value || ''
                                  }
                                  onChange={(e) =>
                                    onNewParameterChange(
                                      analysis._id,
                                      'value',
                                      e.target.value,
                                    )
                                  }
                                  sx={{ flex: 1 }}
                                />
                                <Button
                                  variant="outlined"
                                  onClick={() => onAddParameter(analysis._id)}
                                  sx={{ height: '56px' }}
                                >
                                  Add
                                </Button>
                              </Box>
                              <Box
                                sx={{
                                  mt: 2,
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: 1,
                                }}
                              >
                                {Object.entries(
                                  analysisChain.analysis_parameters?.[
                                    analysis._id
                                  ] || {},
                                ).map(([key, value], index) => {
                                  const chipColor = adjustColorContrast(
                                    getPaletteColor('spectral', index),
                                    theme,
                                  )
                                  return (
                                    <Chip
                                      key={key}
                                      label={`${key}: ${value}`}
                                      onDelete={() =>
                                        onRemoveParameter(analysis._id, key)
                                      }
                                      sx={{
                                        borderColor: chipColor,
                                        color: chipColor,
                                      }}
                                      variant="outlined"
                                    />
                                  )
                                })}
                              </Box>
                            </Stack>
                          </Card>
                        )}
                      </>
                    )}
                  </Stack>
                )
              })}
              <Button
                startIcon={<AddCircleOutlineIcon />}
                onClick={onAddAnalysis}
                disabled={
                  !analysisChain.input_datatype_ids?.[0] ||
                  !hasAvailableSubsequentAnalyses
                }
              >
                Add Subsequent Analysis
              </Button>
            </Stack>
          </Grid>
        </Paper>
      </Grid>

      <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          color="primary"
          type="submit"
          disabled={saving}
          startIcon={
            <Activity mode={saving ? 'visible' : 'hidden'}>
              <CircularProgress size={20} color="inherit" />
            </Activity>
          }
        >
          {saving ? 'Saving...' : 'Save Analysis Chain'}
        </Button>
        <Activity mode={isEditMode ? 'visible' : 'hidden'}>
          <Button
            variant="contained"
            color="secondary"
            onClick={onSaveAsNew}
            disabled={saving}
          >
            Save as new
          </Button>
        </Activity>
      </Box>
    </Box>
  )
}

export default AnalysisChainForm
